import { formatDateForContract, integerToWords, ruInflect } from "@/shared/utils";
import type { CityResponse, RegionResponse } from "@/shared/api/common";
import { getOrganizationTypeDetails, type OrganizationType } from "@/features/legal/shared/utils";
import { renderWorkScheduleText } from "./workScheduleClause";

export interface ContractCityName {
  name_ru: string;
  name_kk: string;
}

export function resolveContractCity(
  workCityId: number | null | undefined,
  cities: CityResponse[],
  regions: RegionResponse[],
): ContractCityName | null {
  if (!workCityId) return null;

  const foundCity = cities.find((c) => c.id === workCityId);
  if (foundCity) return { name_ru: foundCity.name_ru, name_kk: foundCity.name_kk };

  const nationalRegion = regions.find((r) => r.id === workCityId && r.city_type === "national_city");
  if (nationalRegion) return { name_ru: nationalRegion.name_ru, name_kk: nationalRegion.name_kk };

  return null;
}

export function getMonthDeclension(months: number): string {
  if (months === 1) return "месяц";
  if (months >= 2 && months <= 4) return "месяца";
  return "месяцев";
}

export interface TrialPeriodText {
  trialPeriodKk: string;
  trialPeriodRu: string;
}

export function buildTrialPeriodText(
  trialPeriod: boolean | undefined,
  trialDurationMonths: number | null | undefined,
): TrialPeriodText {
  if (!trialPeriod) return { trialPeriodKk: "сынақ мерзімінсіз", trialPeriodRu: "без испытательного срока" };
  if (!trialDurationMonths) return { trialPeriodKk: "", trialPeriodRu: "" };

  const trialPeriodKk = `${trialDurationMonths} (${integerToWords(trialDurationMonths, "kk")}) ай сынақ мерзімін белгілеп`;
  const trialPeriodRu = `с испытательным сроком ${trialDurationMonths} (${integerToWords(trialDurationMonths, "ru")}) ${getMonthDeclension(trialDurationMonths)}`;
  return { trialPeriodKk, trialPeriodRu };
}

export interface SalaryAmountText {
  salaryAmountDigits: string;
  salaryAmountTextKk: string;
  salaryAmountTextRu: string;
}

export function buildSalaryAmountText(salaryAmount: string | number | null | undefined): SalaryAmountText {
  const match = /^(\d+)/.exec(String(salaryAmount ?? "").trim());
  const tenge = match ? Number(match[1]) : 0;
  if (!tenge) {
    return { salaryAmountDigits: "_____________", salaryAmountTextKk: "_____________", salaryAmountTextRu: "_____________" };
  }
  return {
    salaryAmountDigits: String(tenge),
    salaryAmountTextKk: integerToWords(tenge, "kk"),
    salaryAmountTextRu: integerToWords(tenge, "ru"),
  };
}

interface TemplateOrganization {
  name?: string | null;
  bin?: string | null;
  organization_type?: string | null;
  organization_type_display?: { ru: string; kk: string } | null;
  registration_date?: string | null;
  address_ru?: string | null;
  address_kk?: string | null;
  director_basis?: string | null;
  chief_name?: string | null;
  chief_position_ru?: string | null;
  chief_position_kk?: string | null;
}

interface TemplateEmployee {
  fullName?: string | null;
  iin?: string | null;
  idNumber?: string | null;
  idIssuedBy?: string | null;
  idIssueDate?: string | null;
  phone?: string | null;
}

interface ContractTemplateData {
  contract_number?: string | null;
  start_date?: string | null;
  job_position_ru?: string | null;
  job_position_kk?: string | null;
  job_duties_ru?: string[] | null;
  job_duties_kk?: string[] | null;
  trial_period?: boolean;
  trial_duration_months?: number | null;
  work_start_time?: string | null;
  work_end_time?: string | null;
  break_start_time?: string | null;
  break_end_time?: string | null;
  working_days_list?: readonly string[] | null;
  standard_hours_per_day?: number | null;
  salary_amount?: string | number | null;
  employee_address_ru?: string | null;
  employee_address_kk?: string | null;
  work_format?: string | null;
}

interface ContractTemplateSource {
  data: ContractTemplateData;
  organization?: TemplateOrganization | null;
  cityNameRu?: string | null;
  employee: TemplateEmployee;
}

const CHARTER_BASES = ["", "Устав", "Устава"];

function formatShortKazakhDate(date: Date): string {
  return formatDateForContract(date, "kk").replace(" жылғы ", " ж. ");
}

function formatDuties(duties?: string[] | null): string {
  return duties && duties.length > 0 ? `${duties.join(";\n")};` : "";
}

function resolveTypeAbbreviations(organization?: TemplateOrganization | null): { ru: string; kk: string } {
  const fullTitles = { ru: organization?.organization_type_display?.ru ?? "", kk: organization?.organization_type_display?.kk ?? "" };
  if (!organization?.organization_type) return fullTitles;
  try {
    const details = getOrganizationTypeDetails(organization.organization_type as OrganizationType);
    return { ru: details.ru.abbreviation || fullTitles.ru, kk: details.kk.abbreviation || fullTitles.kk };
  } catch {
    return fullTitles;
  }
}

export function buildContractTemplateValues({
  data,
  organization,
  cityNameRu,
  employee,
}: ContractTemplateSource): Record<string, string> {
  const startDate = data.start_date ? new Date(data.start_date) : null;
  const endDate = startDate ? new Date(startDate) : null;
  if (endDate) endDate.setFullYear(endDate.getFullYear() + 1);
  const registrationDate = organization?.registration_date ? new Date(organization.registration_date) : null;
  const idIssueDate = employee.idIssueDate ? new Date(employee.idIssueDate) : null;
  const abbreviations = resolveTypeAbbreviations(organization);
  const { trialPeriodKk, trialPeriodRu } = buildTrialPeriodText(data.trial_period, data.trial_duration_months);
  const isRemote = data.work_format === "remote";
  const basis = (organization?.director_basis ?? "").trim();
  const isCharter = CHARTER_BASES.includes(basis);
  const salaryMatch = /^(\d+)/.exec(String(data.salary_amount ?? "").trim());
  const salaryTenge = salaryMatch ? Number(salaryMatch[1]) : 0;

  return {
    contract_number: data.contract_number ?? "",
    work_city: cityNameRu ?? "",
    start_date_ru: startDate ? formatDateForContract(startDate, "ru") : "",
    start_date_kk: startDate ? formatDateForContract(startDate, "kk") : "",
    end_date_ru: endDate ? formatDateForContract(endDate, "ru") : "",
    end_date_kk: endDate ? formatDateForContract(endDate, "kk") : "",
    organization_name: organization?.name ?? "",
    organization_bin: organization?.bin ?? "",
    organization_address_ru: organization?.address_ru ?? "",
    organization_address_kk: organization?.address_kk ?? "",
    organization_registration_date_ru: registrationDate ? formatDateForContract(registrationDate, "ru", true) : "",
    organization_registration_date_kk: registrationDate ? formatShortKazakhDate(registrationDate) : "",
    organization_type_full_title_ru: organization?.organization_type_display?.ru ?? "",
    organization_type_full_title_kk: organization?.organization_type_display?.kk ?? "",
    organization_type_short_title_ru: abbreviations.ru,
    organization_type_short_title_kk: abbreviations.kk,
    employer_representative: organization?.chief_name ?? "",
    employer_position_ru: organization?.chief_position_ru ?? "",
    employer_position_kk: organization?.chief_position_kk ?? "",
    employer_position_gent_ru: organization?.chief_position_ru ? ruInflect(organization.chief_position_ru, 1) : "",
    representative_action: "действующего",
    employer_basis: isCharter ? "Устава" : basis,
    employer_basis_kk: isCharter ? "Жарғының" : basis,
    employee_full_name: employee.fullName ?? "",
    employee_designation: /^\d{6}[246]/.test(employee.iin ?? "") ? "именуемая" : "именуемый",
    employee_iin: employee.iin ?? "",
    employee_phone: employee.phone ?? "",
    employee_address_registration_ru: data.employee_address_ru ?? "",
    employee_address_registration_kk: data.employee_address_kk ?? "",
    employee_id_number: employee.idNumber ?? "",
    employee_id_issued_by: employee.idIssuedBy ?? "",
    employee_id_issue_date: idIssueDate ? formatDateForContract(idIssueDate, "ru", true) : "",
    employee_id_issue_date_kk: idIssueDate ? formatShortKazakhDate(idIssueDate) : "",
    job_position_gent_ru: data.job_position_ru ? ruInflect(data.job_position_ru, 1) : "",
    job_position_kk: data.job_position_kk ?? "",
    job_description_list_ru: formatDuties(data.job_duties_ru),
    job_description_list_kk: formatDuties(data.job_duties_kk),
    work_schedule_clause_ru: renderWorkScheduleText(data, "ru"),
    work_schedule_clause_kk: renderWorkScheduleText(data, "kk"),
    work_address_ru: (isRemote ? data.employee_address_ru : organization?.address_ru) ?? "",
    work_address_kk: (isRemote ? data.employee_address_kk : organization?.address_kk) ?? "",
    salary_amount: salaryTenge ? String(salaryTenge) : "",
    salary_amount_text_ru: salaryTenge ? integerToWords(salaryTenge, "ru") : "",
    salary_amount_text_kk: salaryTenge ? integerToWords(salaryTenge, "kk") : "",
    trial_period_ru: trialPeriodRu,
    trial_period_kk: trialPeriodKk,
  };
}
